import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, Bell, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const pageContent = {
  '/resources': {
    title: 'Resources',
    subtitle: 'Founder Toolkit',
    description: 'We\'re curating a comprehensive library of resources, templates, and guides to help founders on their entrepreneurial journey. From pitch deck templates to operational playbooks, our resources section will be your go-to toolkit.',
    features: [
      'Pitch deck templates',
      'Financial modeling tools',
      'Operational playbooks',
      'Industry reports & insights'
    ]
  },
  '/events': {
    title: 'Events',
    subtitle: 'Connect & Learn',
    description: 'Join us for exclusive events featuring industry leaders, successful entrepreneurs, and thought leaders. From intimate founder dinners to large-scale conferences, our events bring together the best minds in the ecosystem.',
    features: [
      'Founder meetups',
      'Industry conferences',
      'Expert workshops',
      'Demo days & showcases'
    ]
  },
  '/news': {
    title: 'News',
    subtitle: 'Latest Updates',
    description: 'Stay updated with the latest news about our portfolio companies, fund announcements, and industry insights. We\'re building a dedicated news hub to keep you informed about everything happening in the YourNest ecosystem.',
    features: [
      'Portfolio company updates',
      'Fund announcements',
      'Press coverage',
      'Industry news & trends'
    ],
    externalLink: 'https://yournest.in/news/'
  }
};

const ComingSoonPage = () => {
  const location = useLocation();
  const content = pageContent[location.pathname] || pageContent['/resources'];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-[#C8473A] transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#C8473A]/10 mb-8">
              <Clock className="w-10 h-10 text-[#C8473A]" />
            </div>

            {/* Badge */}
            <div className="inline-block px-4 py-2 bg-[#C8473A]/10 rounded-full text-yn-brand-on-tint text-sm font-medium mb-6">
              {content.subtitle}
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
              {content.title} <span className="text-[#C8473A]">Coming Soon</span>
            </h1>

            {/* Description */}
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              {content.description}
            </p>

            {/* Features Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-12"
            >
              {content.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100"
                >
                  <div className="w-2 h-2 rounded-full bg-[#C8473A]" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Section */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Bell className="w-5 h-5 text-[#C8473A]" />
                <h3 className="text-lg font-semibold text-[#211F1F]">Stay Updated</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Be the first to know when we launch this section.
              </p>
              
              {content.externalLink ? (
                <div className="space-y-4">
                  <Button
                    asChild
                    className="bg-[#C8473A] hover:bg-[#B74F40] text-white px-8 rounded-full"
                  >
                    <a href={content.externalLink} target="_blank" rel="noopener noreferrer">
                      View News on YourNest.in
                    </a>
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Button
                    asChild
                    className="bg-[#C8473A] hover:bg-[#B74F40] text-white px-8 rounded-full"
                  >
                    <a href="mailto:info@yournest.in?subject=Notify me about new updates">
                      <Mail className="w-4 h-4 mr-2" />
                      Get Notified
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-[#211F1F] text-[#211F1F] hover:bg-[#211F1F] hover:text-white rounded-full"
                  >
                    <Link to="/insights">
                      Browse Insights
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ComingSoonPage;
